import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import Modal from '@/components/Modal';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import LoadingState from '@/components/LoadingState';
import { Body } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import {
  WorkerAccountabilityViolationV2,
  ViolationDisplayStatus,
} from '@/types/accountability';
import {
  getDisplayStatusBadge,
  getViolationTypeBadge,
} from '@/routes/Tasks/shared/violationBadges';

type Props = {
  workerId: number;
  onClose: () => void;
};

const headerFields = ['Shift Name', 'Type', 'Date', 'Status', ''];

const ViolationsModal = ({ workerId, onClose }: Props) => {
  const navigate = useNavigate();
  const [violations, setViolations] = useState<
    WorkerAccountabilityViolationV2[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadViolations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getViolationsV2({
        worker_id: workerId,
        per_page: 100,
        sort_by: 'occurred_at',
        sort_order: 'desc',
      });

      if (response.success) {
        setViolations(
          (response.violations || []) as WorkerAccountabilityViolationV2[],
        );
      } else {
        // A falsy `success` used to be ignored, leaving `violations` empty and
        // the modal saying "No violations found" — the same wrong claim as the
        // swallowed exception below.
        setViolations([]);
        setError('Their violations could not be loaded.');
      }
    } catch {
      // Was `// silently fail`. Swallowing it made a failed request render as
      // "No violations found for this worker" — a definitive statement about
      // someone's accountability record, produced by a request that never
      // answered. An admin checking whether a worker has violations would be
      // told there are none.
      setViolations([]);
      setError('Their violations could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  const handleRowClick = (violationId: number) => {
    navigate(`/tasks/violations/${violationId}`);
  };

  return (
    <Modal
      disableClickout
      size="lg"
      title="Violations"
      onRequestClose={onClose}
    >
      <Card.Section>
        {loading ? (
          <LoadingState overlayColor="white" />
        ) : error ? (
          <Stack
            vertical
            gap={8}
            style={{ padding: '24px', alignItems: 'center' }}
          >
            <Body color="danger">{error}</Body>
            <Body color="light">
              This is not a record that they have none — the request failed.
            </Body>
            <Button
              a11yLabel="Retry loading violations"
              appearance="outline"
              label="Retry"
              onClick={loadViolations}
            />
          </Stack>
        ) : violations.length === 0 ? (
          <Stack style={{ padding: '24px', justifyContent: 'center' }}>
            <Body color="light">No violations found for this worker.</Body>
          </Stack>
        ) : (
          <Table>
            <TableHeader fields={headerFields} />
            <tbody>
              {violations.map((violation) => (
                <TableRow
                  key={violation.id}
                  onClick={() => handleRowClick(violation.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Body weight="medium">
                      {violation.skill_name || `Shift #${violation.job_id}`}
                    </Body>
                  </TableCell>
                  <TableCell>
                    {getViolationTypeBadge(violation.violation_type)}
                  </TableCell>
                  <TableCell>
                    <Body>
                      {format(new Date(violation.occurred_at), 'MM/dd/yyyy')}
                    </Body>
                  </TableCell>
                  <TableCell>
                    {getDisplayStatusBadge(
                      violation.display_status as ViolationDisplayStatus,
                    )}
                  </TableCell>
                  <TableCell>
                    <Body color="theme" weight="medium">
                      View Details
                    </Body>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Section>
    </Modal>
  );
};

export default ViolationsModal;
